import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RecentlyViewed from './RecentlyViewed';
import { Helmet } from 'react-helmet';
import { getStoredZip, isValidZip, persistRecentZip, requestLocationZip } from './Utils/locationZip';

function HomePage() {
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [radius] = useState('25');
  const [zip, setZip] = useState('');
  const [error, setError] = useState('');
  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const searchSectionRef = useRef(null);
  const makeSelectRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE}/cars/makes`)
      .then(res => res.json())
      .then(data => setMakeOptions(data));
  }, []);

  useEffect(() => {
    if (make) {
      fetch(`${process.env.REACT_APP_API_BASE}/cars/models?make=${encodeURIComponent(make)}`)
        .then(res => res.json())
        .then(data => setModelOptions(data));
    } else {
      setModelOptions([]);
    }
  }, [make]);

  useEffect(() => {
    if (isValidZip(zip)) return;
    if (window.location.hostname === '127.0.0.1') return;
    const savedZip = getStoredZip();
    if (isValidZip(savedZip)) {
      setZip((currentZip) => isValidZip(currentZip) ? currentZip : savedZip);
      return;
    }
    let cancelled = false;
    requestLocationZip().then((locatedZip) => {
      if (cancelled || !isValidZip(locatedZip)) return;
      setZip((currentZip) => isValidZip(currentZip) ? currentZip : locatedZip);
    });
    return () => { cancelled = true; };
    // Location initialization runs only when this homepage instance is entered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  sessionStorage.setItem('cachedCars', []);
  sessionStorage.setItem('cachedTotalPages', '0');
  sessionStorage.setItem('cachedTotalResults', '0');
  sessionStorage.setItem('cachedPage', "");
  sessionStorage.setItem('cachedScroll', '0');

  const handleSearch = () => {
    if (!zip) {
      setError('ZIP code is required.');
      return;
    }
    if (!/^[0-9]{5}$/.test(zip)) {
      setError('ZIP must be 5 digits.');
      return;
    }
    if (!make) {
      setError('Please select a make before searching.');
      return;
    }

    setError('');
    persistRecentZip(zip);
    const query = new URLSearchParams();
    if (make) query.append('make', make);
    if (model) query.append('model', model);
    if (radius) query.append('radius', radius);
    query.append('zip', zip);
    window.gtag?.('event', 'search_submit', {
      make,
      model,
      radius,
    });
    navigate(`/search?${query.toString()}`);
  };

  const zipOrDefault = () => isValidZip(zip) ? zip.trim() : getStoredZip() || '33131';

  const goToSearch = (params = {}) => {
    const q = new URLSearchParams();
    if (params.make) q.append('make', params.make);
    if (params.model) q.append('model', params.model);
    q.append('radius', params.radius || radius || '25');
    q.append('zip', params.zip || zipOrDefault());
    window.gtag?.('event', 'search_submit', {
      make: params.make || '',
      model: params.model || '',
      radius: params.radius || radius || '25',
    });
    navigate(`/search?${q.toString()}`);
  };

  const handleQuickPreset = (mk, mdl) => {
    goToSearch({ make: mk, model: mdl });
  };

  const handleSearchAllClick = () => {
    window.gtag?.('event', 'homepage_search_all_click', {
      destination: 'homepage_inventory_search',
    });
    searchSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => makeSelectRef.current?.focus(), 350);
  };

  const handleBestDealsClick = (event) => {
    const zipForBestDeals = isValidZip(zip) ? zip.trim() : getStoredZip();
    const destination = zipForBestDeals
      ? `/insights/best-deals?zip=${encodeURIComponent(zipForBestDeals)}`
      : '/insights/best-deals';

    window.gtag?.('event', 'homepage_best_deals_click', {
      destination: '/insights/best-deals',
      zip_passed: Boolean(zipForBestDeals),
      zip_source: isValidZip(zip) ? 'homepage_field' : zipForBestDeals ? 'stored_search' : 'none',
    });

    event.preventDefault();
    navigate(destination);
  };

  const handleCheckDealClick = (source = 'homepage_tools') => {
    window.gtag?.('event', 'homepage_check_deal_click', {
      destination: '/deal-checker',
      source,
    });
  };

  const popularSearches = [
    { make: 'ACURA', model: 'MDX' },
    { make: 'Toyota', model: 'Camry' },
    { make: 'Tesla', model: 'Model 3' },
    { make: 'Honda', model: 'Civic' },
    { make: 'Ford', model: 'F-150' },
    { make: 'Chevrolet', model: 'Silverado' },
  ];

  const trustCards = [
    { title: 'Sold Vehicle Comparisons', desc: 'See active listings next to recent comparable sales.' },
    { title: 'Meshum Estimate', desc: 'Use comp-based pricing insight before contacting dealers.' },
    { title: 'Transparent Pricing', desc: 'Understand how asking prices compare with the market.' },
    { title: 'Nationwide Inventory', desc: 'Start local and widen the search when you need more options.' },
  ];

  const steps = [
    { title: 'Choose your car', text: 'Select a make, model, and ZIP to start with focused results.' },
    { title: 'Review the market', text: 'Compare listings with sold comps and price signals.' },
    { title: 'Move with confidence', text: 'Contact dealers knowing where the price should land.' },
  ];

  return (
    <>
      <Helmet>
        <title>Best Used Car Deals and Sold Price Comparisons | Meshum</title>
        <meta
          name="description"
          content="Find strong-value used car listings by comparing today's asking prices with recently sold vehicles. Browse best deals or search all cars on Meshum."
        />
        <link rel="canonical" href="https://meshum.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Best Used Car Deals and Sold Price Comparisons | Meshum" />
        <meta
          property="og:description"
          content="Find strong-value used car listings by comparing today's asking prices with recently sold vehicles."
        />
        <meta property="og:url" content="https://meshum.com/" />
        <meta property="og:image" content="https://meshum.com/share-thumbnail.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Best Used Car Deals and Sold Price Comparisons | Meshum" />
        <meta
          name="twitter:description"
          content="Find strong-value used car listings by comparing today's asking prices with recently sold vehicles."
        />
        <meta name="twitter:image" content="https://meshum.com/share-thumbnail.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Meshum",
            url: "https://meshum.com",
            description:
              "Find strong-value used car listings by comparing today's asking prices with recently sold vehicles. Browse best deals or search all cars on Meshum."
          })}
        </script>
      </Helmet>

      <main className="bg-slate-50">
        <section className="relative isolate overflow-visible bg-slate-950 text-white">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-40"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1489824904134-891ab64532f1?auto=format&fit=crop&w=1800&q=80')" }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/92 to-slate-950/55" aria-hidden="true" />

          <div className="relative mx-auto grid max-w-7xl gap-4 px-4 py-5 sm:px-6 sm:py-7 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-10 lg:py-12">
            <div className="flex flex-col">
              <div className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-100">
                Real sale prices for smarter car buying
              </div>
              <h1 className="mt-2 max-w-3xl text-4xl font-semibold leading-tight text-white sm:mt-4 sm:text-5xl">
                Find the Best Deals on Used Cars.
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-200 sm:mt-3 sm:text-lg sm:leading-7">
                Compare today's listings with real sold prices so you can see which cars offer the strongest value before you buy.
              </p>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/insights/best-deals"
                  onClick={handleBestDealsClick}
                  className="inline-flex h-12 items-center justify-center rounded-lg bg-emerald-500 px-5 text-base font-semibold text-slate-950 shadow-lg shadow-emerald-950/30 transition hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  See Today's Best Deals
                </Link>
                <button
                  type="button"
                  onClick={handleSearchAllClick}
                  className="inline-flex h-12 items-center justify-center rounded-lg border border-white/30 bg-white/10 px-5 text-base font-semibold text-white transition hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/50"
                >
                  Search All Cars
                </button>
              </div>

            </div>

            <div
              ref={searchSectionRef}
              id="homepage-inventory-search"
              className="rounded-xl border border-slate-200 bg-white p-5 text-slate-900 shadow-[0_30px_90px_rgba(15,23,42,0.38)] ring-1 ring-slate-200 sm:p-6 lg:justify-self-end lg:w-full lg:max-w-[560px]"
            >
              <div className="mb-4">
                <h2 className="text-2xl font-semibold text-slate-950">Search local inventory</h2>
                <p className="mt-1 text-sm text-slate-600">Start with a make and ZIP code. Model is optional.</p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Make</label>
                  <select
                    ref={makeSelectRef}
                    value={make}
                    onChange={(e) => {
                      setMake(e.target.value);
                      setModel('');
                    }}
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">All Makes</option>
                    {makeOptions.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Model</label>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    disabled={!make}
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-slate-100 disabled:text-slate-500"
                  >
                    <option value="">All Models</option>
                    {modelOptions.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">ZIP code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter ZIP code"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>

              {error && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                onClick={handleSearch}
                className="mt-4 flex h-12 w-full items-center justify-center rounded-lg bg-blue-600 px-4 text-base font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                Search cars
              </button>

              <p className="mt-3 text-center text-xs text-slate-500">
                Results open in the existing search experience.
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 shadow-sm sm:flex sm:items-center sm:justify-between sm:gap-6">
              <div className="max-w-3xl">
                <h2 className="text-xl font-semibold text-slate-950 sm:text-2xl">Have a VIN? See what the car is worth. → Have a VIN? Check if it's a good deal.</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">
                  Get a Meshum Estimate based on recently sold comparable vehicles. → Compare the asking price against real sold comps with a Meshum Estimate.
                </p>
              </div>
              <Link
                to="/deal-checker"
                onClick={() => handleCheckDealClick('homepage_car_value_callout')}
                className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 sm:mt-0 sm:w-auto sm:shrink-0"
              >
                Check This Deal →
              </Link>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
            <div className="max-w-3xl">
              <h2 className="text-2xl font-semibold text-slate-950 sm:text-3xl">Understand the market before you buy.</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">
                Meshum helps buyers compare live listings with sold vehicles so every search starts from a realistic price view.
              </p>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {trustCards.map((item) => (
                <div key={item.title} className="rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-sm">
                  <h3 className="text-base font-semibold text-slate-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-950 sm:text-2xl">Browse by make</h2>
              <p className="mt-1 text-sm text-slate-600">Quick-start searches use your ZIP when available.</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {popularSearches.map((q) => (
              <button
                key={`${q.make}-${q.model}`}
                onClick={() => handleQuickPreset(q.make, q.model)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-left text-sm font-semibold text-slate-900 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
              >
                <span className="block text-xs font-medium uppercase text-slate-500">{q.make}</span>
                {q.model}
              </button>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <h2 className="text-3xl font-semibold text-slate-950">A clearer path from search to offer</h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                The homepage gets buyers into a focused search quickly, then the existing results experience carries the comparison workflow.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {steps.map((step, index) => (
                <div key={step.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-sm font-semibold text-white">
                    {index + 1}
                  </div>
                  <h3 className="text-base font-semibold text-slate-950">{step.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-900 px-4 py-10 text-white sm:px-6 sm:py-12">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center">
            <div>
              <h2 className="text-3xl font-semibold text-white">Built for trust before the test drive.</h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Meshum presents market evidence in plain language so buyers can understand whether a listing is worth pursuing.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <details className="rounded-lg border border-white/10 bg-white/10 p-4">
                <summary className="cursor-pointer font-semibold">Do I need an account to browse?</summary>
                <p className="mt-2 text-sm leading-6 text-slate-300">No. You can search and review listings without signing up.</p>
              </details>
              <details className="rounded-lg border border-white/10 bg-white/10 p-4">
                <summary className="cursor-pointer font-semibold">What makes Meshum different?</summary>
                <p className="mt-2 text-sm leading-6 text-slate-300">The search experience centers real sold-price comparisons alongside active listings.</p>
              </details>
              <details className="rounded-lg border border-white/10 bg-white/10 p-4">
                <summary className="cursor-pointer font-semibold">How local are results?</summary>
                <p className="mt-2 text-sm leading-6 text-slate-300">Searches use ZIP and radius parameters to keep the market context relevant.</p>
              </details>
              <details className="rounded-lg border border-white/10 bg-white/10 p-4">
                <summary className="cursor-pointer font-semibold">Can I contact dealers?</summary>
                <p className="mt-2 text-sm leading-6 text-slate-300">Yes. Existing listing and dealer contact flows remain unchanged.</p>
              </details>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-8">
            <h2 className="text-2xl font-semibold text-slate-950">Ready to compare your local market?</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Choose a make above, optionally pick a model, or start with a popular search and refine in the existing results page.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              {popularSearches.slice(0, 3).map((q) => (
                <button
                  key={`cta-${q.make}-${q.model}`}
                  onClick={() => handleQuickPreset(q.make, q.model)}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  {q.make} {q.model}
                </button>
              ))}
            </div>
          </div>
        </section>

        <RecentlyViewed cars={JSON.parse(localStorage.getItem('recentlyViewedCars') || '[]')} />
      </main>
    </>
  );
}

export default HomePage;
